// ============================================================================
// connection.ts — ЕДИНЫЙ менеджер подключения к серверу (SSoT, событийная
// модель, БЕЗ поллинга):
//   - живость сервера = живой стрим EventBus (хаб сообщает online/reconnecting);
//   - navigator.onLine — сеть устройства (offline);
//   - статус читают ВСЕ индикаторы (PubConnectionIndicator, StatusBar, чат);
//   - автореконнект: хаб переподключается сам; при восстановлении клиенты
//     (чат и др.) перезагружают данные по событию — без таймеров.
// ============================================================================

import { create } from 'zustand';
import { eventBusHub } from '@/lib/eventBusHub';
import { isForcedOffline, markLinkDown, subscribeOfflineFlag } from '@/lib/offlineFlag';

export type ConnStatus = 'online' | 'offline' | 'reconnecting';
export type RealtimeStatus = 'online' | 'offline' | 'reconnecting';

interface ConnectionState {
  /** HTTP-доступность сервера: именно она определяет офлайн-запись/кэш. */
  status: ConnStatus;
  /** Отдельно: живой поток событий. Его падение НЕ означает офлайн. */
  realtime: RealtimeStatus;
  set: (s: ConnStatus) => void;
  setRealtime: (s: RealtimeStatus) => void;
}

export const useConnectionStore = create<ConnectionState>((set) => ({
  status: 'online',
  realtime: 'online',
  set: (s) => set({ status: s }),
  setRealtime: (s) => set({ realtime: s }),
}));

let inited = false;
let networkUp = typeof navigator !== 'undefined' ? navigator.onLine : true;

/** Смена статуса с защитой от ложных сигналов и лишних перерисовок. */
function setStatus(s: ConnStatus): void {
  // В офлайн-режим (кэш/очередь) уводит только реальная недоступность HTTP.
  // reconnecting — это живый сервер без realtime-потока; писать в сервер можно.
  markLinkDown(s === 'offline');
  const st = useConnectionStore.getState();
  if (st.status === s) return;
  // «Сервер доступен» без сети устройства невозможно — не врём индикатором.
  if (s === 'online' && !networkUp) return;
  st.set(s);
}

function setStatusUnlessOffline(s: ConnStatus): void {
  if (useConnectionStore.getState().status !== 'offline') setStatus(s);
}

/**
 * ДЕГРАДАЦИЯ В ОФЛАЙН: транспортный сбой (связь с сервером потеряна) —
 * единая точка перевода статуса. Вызывается из dataService/чата при
 * транспортных ошибках запросов (серверные ошибки сюда НЕ попадают).
 * Возврат в online — только по подтверждению живым стримом EventBus.
 */
export function markConnectionOffline(): void {
  setStatus('offline');
}

/** Инициализация (вызывается один раз при старте приложения). */
export function initConnectionManager(): void {
  if (inited) return;
  inited = true;

  // «Автономный режим» (галочка в Профиле): связь сознательно отключена —
  // статус offline; выключение — ждём подтверждения стримом (poke).
  if (isForcedOffline()) setStatus('offline');
  subscribeOfflineFlag(() => {
    if (isForcedOffline()) {
      setStatus('offline');
    } else {
      setStatusUnlessOffline('reconnecting');
      eventBusHub.poke();
    }
  });

  // Поток событий — это realtime-канал, НЕ индикатор доступности сервера.
  // Если он упал, HTTP/RPC/уведомления могут продолжать работать; поэтому
  // статус офлайн из-за него не ставим.
  eventBusHub.onStatus((s) => {
    useConnectionStore.getState().setRealtime(s);
    if (s === 'online' && !isForcedOffline()) {
      // Поток поднялся — сервер точно доступен.
      setStatus('online');
    }
  });

  const probe = () => { void probeConnection(); };

  // Сеть устройства (браузер знает точно)
  const onLine = () => {
    networkUp = true;
    if (isForcedOffline()) return;
    setStatusUnlessOffline('reconnecting');
    probe();
    eventBusHub.poke();
  };
  const offLine = () => {
    networkUp = false;
    setStatus('offline');
  };
  const probeIfNeeded = () => {
    if (!isForcedOffline() && useConnectionStore.getState().status !== 'online') probe();
  };
  window.addEventListener('online', onLine);
  window.addEventListener('offline', offLine);
  window.addEventListener('focus', probeIfNeeded);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) probeIfNeeded();
  });
}

/** Проверить именно HTTP-доступность сервера (не realtime-поток). */
export async function probeConnection(): Promise<boolean> {
  if (isForcedOffline()) {
    setStatus('offline');
    return false;
  }
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    const response = await fetch('/health', {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal,
    }).finally(() => clearTimeout(timer));
    if (!response.ok) throw new Error(`health ${response.status}`);
    networkUp = true;
    setStatus('online');
    eventBusHub.poke();
    return true;
  } catch {
    setStatus('offline');
    return false;
  }
}
